<template>
  <OnboardingStep
    heading="Billing"
    :commit="commit"
    :disable-next="!isReady || isPaymentFormLoading"
    :form-id="formId"
    :next-button="nextButtonText"
  >
    <template #header>
      <p>
        <template v-if="selectedPlan">
          You’ve selected the <SubscriptionLevelName :level="selectedPlan" /> plan - great choice!
        </template>
        Let’s get your account and payment information set up.
      </p>
    </template>
    <template #default="{ next }">
      <template v-if="!isReady">
        <p>Loading, please wait...</p>
      </template>
      <Card v-else heading="Enter Your Billing Information" heading-style="3">
        <BaseForm v-if="paymentMethod" :verb="null">
          <RadioField v-model="showPaymentForm" :value="false">
            <CreditCard v-if="paymentCard" :brand="paymentCard.brand" :number="paymentCard.last4" />
            <template v-else>Use my payment method on file</template>
          </RadioField>
          <RadioField v-model="showPaymentForm" :value="true">
            Use a different payment method
          </RadioField>
        </BaseForm>
        <PaymentMethodForm
          v-if="showPaymentForm"
          v-model="newPaymentMethod"
          :id="formId"
          :loading.sync="isPaymentFormLoading"
          :verb="null"
          @save="next"
        />
      </Card>
    </template>
    <template #sidebar>
      <div class="card-billing-cycle card-v2">
        <h3>Billing Cycle</h3>
        <label :class="{ selected: billingPeriod === 'monthly' }" class="billing-cycle-option flex">
          <input type="radio" v-model="billingPeriod" value="monthly" style="display: none" />
          Monthly
          <div v-if="selectedPlan && !selectedPlan.isFree" class="align-right">
            <SubscriptionLevelPrice :level="selectedPlan" period="monthly" />
          </div>
        </label>
        <label :class="{ selected: billingPeriod === 'yearly' }" class="billing-cycle-option flex">
          <input type="radio" v-model="billingPeriod" value="yearly" style="display: none" />
          Yearly
          <div v-if="selectedPlan && !selectedPlan.isFree" class="align-right flex">
            <div v-if="selectedPlan.yearlyDiscount > 0" class="save-badge">
              Save {{ formatPercentage(selectedPlan.yearlyDiscount) }}
            </div>
            <SubscriptionLevelPrice :level="selectedPlan" period="yearly" />
          </div>
        </label>
      </div>
    </template>
    <template #before-actions v-if="selectedPlan && !selectedPlan.isFree">
      <p class="onboarding-disclaimer">
        By clicking “{{ nextButtonText }}”, you agree that your payment method will be charged
        <Price
          :amount="
            billingPeriod === 'yearly'
              ? selectedPlan.yearlyPricePerYear
              : selectedPlan.monthlyPricePerMonth
          "
        />, plus applicable tax, per {{ billingPeriod === 'yearly' ? 'year' : 'month' }}.
      </p>
    </template>
  </OnboardingStep>
</template>

<script>
import {
  capitalize,
  toBillingPeriod,
  toFiniteNumber,
  toInteger,
  toString,
} from '@/helpers/convert';
import onboarding from '@/mixins/onboarding';
import SubscriptionLevel from '@/models/SubscriptionLevel';

import BaseForm from '@/components/forms/BaseForm';
import CreditCard from '@/components/billing/CreditCard';
import PaymentMethodForm from '@/components/forms/PaymentMethodForm';

export default {
  name: 'OnboardingBilling',
  mixins: [onboarding],
  components: { BaseForm, CreditCard, PaymentMethodForm },
  data() {
    return {
      /** @type {boolean} */
      isPaymentFormLoading: false,
      /** @type {boolean} */
      isReady: false,
      /** @type {stripe.paymentMethod.PaymentMethod | null} */
      newPaymentMethod: null,
      /** @type {stripe.paymentMethod.PaymentMethod | null} */
      paymentMethod: null,
      /** @type {boolean} */
      showPaymentFormData: true,
    };
  },
  computed: {
    /**
     * @returns {Intl.NumberFormat['format']}
     */
    formatCurrency() {
      return this.$getCurrencyFormat().format;
    },
    /**
     * @returns {Intl.NumberFormat['format']}
     */
    formatPercentage() {
      return this.$getNumberFormat({ style: 'percent' }).format;
    },
    /**
     * @returns {string}
     */
    formId() {
      return this.showPaymentForm ? 'onboarding-step-form' : null;
    },
    name: {
      /**
       * @returns {string | null}
       */
      get() {
        return this.billingDetails.name;
      },
      /**
       * @param {string | null} value
       * @returns {void}
       */
      set(value) {
        this.billingDetails.name = value;
      },
    },
    /**
     * @returns {string}
     */
    nextButtonText() {
      return 'Submit';
    },
    /**
     * @returns {stripe.paymentMethod.PaymentMethodCard | null}
     */
    paymentCard() {
      return this.paymentMethod?.card || null;
    },
    selectedPlan: {
      /**
       * @returns {SubscriptionLevel | null}
       */
      get() {
        return this.$store.getters['onboarding/plan'];
      },
      /**
       * @param {SubscriptionLevel | null} value
       * @returns {void}
       */
      set(value) {
        this.$store.commit('onboarding/setPlan', value);
      },
    },
    selectedPlanIndex: {
      /**
       * @returns {number}
       */
      get() {
        return this.$store.getters['onboarding/planIndex'];
      },
      /**
       * @param {number} index
       */
      set(index) {
        this.$store.commit('onboarding/setPlanIndex', index);
      },
    },
    showPaymentForm: {
      /**
       * @returns {boolean}
       */
      get() {
        return !!this.showPaymentFormData || !this.paymentMethod;
      },
      /**
       * @param {boolean} value
       * @returns {void}
       */
      set(value) {
        this.showPaymentFormData = !!value;
      },
    },
  },
  methods: {
    /**
     * @returns {Promise<boolean | void>}
     */
    async commit() {
      if (!this.isReady) {
        return false;
      }

      /** @type {typeof this & { billingPeriod: BillingPeriod }} */
      const { billingPeriod, selectedPlan: plan } = this;
      if (!plan) {
        this.$router.push({ name: this.$routes.OnboardingUpgradeRole.name });
        return false;
      }

      const store = this.$store;

      // Update subscription
      /** @type {Subscription | null} */
      let subscription = await store.dispatch('subscriptions/updateMySubscription', {
        billingPeriod,
        roleId: plan.roleId,
      });

      let delay = 500;
      let attempts = 1;

      /** @type {() => Promise<void>} */
      const forSomeTime = () =>
        new Promise((r) => {
          setTimeout(r, delay);
          delay += 500;
        });
      const maxAttempts = 10;
      /** @type {() => Promise<Subscription | null>} */
      const reloadSubscription = store.dispatch.bind(this, 'subscriptions/getMySubscription', true);

      while (
        (!subscription || subscription.isDeactivated || subscription.isTrialing) &&
        attempts++ <= maxAttempts
      ) {
        /**
         * It may take a few moments for the subscription change to propagate across Stripe's
         * systems and for them to send the appropriate web hooks to back end. This loop keeps
         * refreshing the subscription data until its `isDeactivated` and `isTrialing` properties
         * are both false.
         */
        await forSomeTime();
        subscription = await reloadSubscription();
      }

      await store.dispatch('users/getActiveUser', true);

      /**
       * Dispatch Google Tag Manager "Order Confirmation - New Member" event
       *
       * Don't `await` the statement below; we don't want to hold up the completion of onboarding
       * just to send some data to Google Tag Manager.
       */
      this.dispatchGtmOrderConfirmationEvent(billingPeriod, plan, this.selectedPlanIndex);

      return true;
    },
    /**
     * Dispatches the `Order Confirmation - New Member` Google Tag Manager custom event.
     *
     * @param {BillingPeriod} billingPeriod
     * The billing period; either `monthly` or `yearly`.
     *
     * @param {SubscriptionLevel} plan
     * The selected plan.
     *
     * @param {number} planIndex
     * The zero-based index of the {@link plan `plan`} within the list from which it was chosen.
     *
     * @returns {Promise<boolean>}
     * A promise that fulfills as soon as either
     * 1. the event is sent successfully (in which case the promise is fulfilled with `true`), or
     * 2. the event times out (in which case the promise is fulfilled with `false`).
     */
    async dispatchGtmOrderConfirmationEvent(billingPeriod, plan, planIndex) {
      if (
        !(billingPeriod = toBillingPeriod(billingPeriod, null)) ||
        !(plan = SubscriptionLevel.from(plan))
      ) {
        return false;
      }

      /** @type {DataLayerEntry | null} */
      let eventData;
      if (plan.isFree) {
        /*
         * For free plans, no transaction takes place; but because Google Analytics Ecommerce
         * Tracking is expecting a transaction ID and will break without it, Michael from
         * Vuja De has given his blessing to generate a fake transaction ID in this scenario.
         */
        let transactionId = String(Date.now());
        const userId = toString(this.$activeUser?.id);
        if (userId) {
          transactionId += `-${userId.slice(0, 8)}`;
        }
        eventData = this.getGtmOrderConfirmationEventData(
          billingPeriod,
          plan,
          planIndex,
          transactionId,
          0,
          0
        );
      } else {
        /*
         * For paid plans, the subscription should be activated in Stripe by now. Try to get
         * the matching invoice and use it to generate the event.
         */
        const dataFromInvoice = this.getGtmOrderConfirmationEventDataFromInvoice.bind(
          this,
          billingPeriod,
          plan,
          planIndex
        );
        const store = this.$store;
        /** @type {stripe.api.Invoice | null | undefined} */
        let invoice = await store.dispatch('invoices/getMyNextInvoice');

        if (!(eventData = dataFromInvoice(invoice))) {
          /** @type {(stripe.api.Invoice | null | undefined)[] | null | undefined} */
          const invoices = await store.dispatch('invoices/getMyInvoices');
          if (Array.isArray(invoices)) {
            for (invoice of invoices) {
              if ((eventData = dataFromInvoice(invoice))) {
                break;
              }
            }
          }
        }
      }

      return eventData
        ? this.$gtmDispatchEvent('Order Confirmation - New Member', eventData)
        : false;
    },
    /**
     * Creates and returns the `dataLayer` object for the `Order Confirmation - New Member`
     * Google Tag Manager custom event based on the given parameters.
     *
     * @param {BillingPeriod} billingPeriod
     * The billing period; either `monthly` or `yearly`.
     *
     * @param {SubscriptionLevel} plan
     * The selected plan.
     *
     * @param {number} planIndex
     * The zero-based index of the {@link plan `plan`} within the list from which it was chosen.
     *
     * @param {string} transactionId
     * The transaction ID.
     *
     * @param {number | null} revenue
     * The revenue amount.
     *
     * @param {number | null} tax
     * The tax amount.
     *
     * @returns {DataLayerEntry}
     */
    getGtmOrderConfirmationEventData(billingPeriod, plan, planIndex, transactionId, revenue, tax) {
      if (
        !(billingPeriod = toBillingPeriod(billingPeriod, null)) ||
        !(plan = SubscriptionLevel.from(plan)) ||
        !(transactionId = toString(transactionId))
      ) {
        return null;
      }
      return {
        ecommerce: {
          purchase: {
            actionField: {
              id: transactionId,
              affiliation: 'New Subscription',
              revenue: toFiniteNumber(revenue) || 0,
              tax: toFiniteNumber(tax) || 0,
              shipping: 0,
              coupon: '',
            },
            products: [
              {
                id: plan.name,
                name: plan.name,
                price: plan[`${billingPeriod}PricePerMonth`],
                brand: plan.name,
                category: plan.isFree ? plan.name : capitalize(billingPeriod),
                variant: '',
                position: toInteger(planIndex, 0) ?? -1,
                quantity: 1,
              },
            ],
          },
        },
      };
    },
    /**
     * Creates and returns the `dataLayer` object for the `Order Confirmation - New Member`
     * Google Tag Manager custom event based on the provided invoice, or `null` if the
     * provided invoice is invalid.
     *
     * @param {BillingPeriod} billingPeriod
     * The billing period; either `monthly` or `yearly`.
     *
     * @param {SubscriptionLevel} plan
     * The selected plan.
     *
     * @param {number} planIndex
     * The zero-based index of the {@link plan `plan`} within the list from which it was chosen.
     *
     * @param {stripe.api.Invoice | null | undefined} invoice
     * The invoice.
     *
     * @returns {DataLayerEntry | null}
     */
    getGtmOrderConfirmationEventDataFromInvoice(billingPeriod, plan, planIndex, invoice) {
      const currency = this.$defaultCurrency;
      if (
        !(billingPeriod = toBillingPeriod(billingPeriod, null)) ||
        !(plan = SubscriptionLevel.from(plan)) ||
        !invoice ||
        this.$toCurrencyCode(invoice.currency) !== currency
      ) {
        return null;
      }

      const interval = billingPeriod === 'monthly' ? 'month' : 'year';
      const item = invoice.lines.data.find((item) => {
        /** @type {stripe.api.Plan | undefined} */
        let itemPlan;
        return !!(
          item &&
          item.type === 'subscription' &&
          (itemPlan = item.plan) &&
          this.$toCurrencyCode(itemPlan.currency) === currency &&
          itemPlan.interval === interval
        );
      });

      if (!item) {
        return null;
      }

      /** @type {string | number | { id: string | number } | null | undefined} */
      let transactionId = toString(invoice.number, '') || toString(invoice.id, '');
      if (!transactionId) {
        transactionId = item.subscription_item;
        if (transactionId && typeof transactionId === 'object') {
          transactionId = transactionId.id;
        }
        transactionId = toString(transactionId, '');
      }

      const revenue = this.$toCurrencyAmountFromPaise(invoice.total, currency);
      const tax = this.$toCurrencyAmountFromPaise(invoice.tax, currency) || 0;

      return this.getGtmOrderConfirmationEventData(
        billingPeriod,
        plan,
        planIndex,
        transactionId,
        revenue,
        tax
      );
    },
  },
  async mounted() {
    const store = this.$store;
    /** @type {stripe.paymentMethod.PaymentMethod | null} */
    let paymentMethod = null;
    try {
      paymentMethod = await store.dispatch('payments/getMyPaymentMethod', true);
    } catch (e) {} // eslint-disable-line no-empty
    this.paymentMethod = paymentMethod;
    this.showPaymentForm = !paymentMethod;
    this.isReady = true;
  },
};
</script>
